"use client";

/**
 * The one analytics lifecycle owner, mounted inside the root auth-session
 * boundary so its `me` fetch is identity-scoped and torn down with the session. Owns, in order: first-touch capture →
 * identity convergence via `reconcileIdentity` (reset-then-identify lives in
 * core; the sign-out path also reconciles imperatively so cleanup survives an
 * immediate tab unload) → synthetic pageview. Marketing routes get anonymous
 * pageviews; `/app` waits for auth resolution so identify always precedes
 * the pageview (observability.md).
 *
 * It also records, from the same `me` and without waiting for the SDK, the ad
 * pixels' internal-account verdict (`adPixels/internalAccount.ts`, card #1113).
 */
import { isInternalAccountEmail } from "@reflect/domain";
import { useQuery } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useEffect } from "react";

import { rememberAccountTraffic } from "../adPixels/internalAccount";
import { useTRPC } from "../trpc/context";
import { useAuth } from "../trpc/provider";
import { getAnalytics } from "./index";

/**
 * Drive the analytics lifecycle for the whole app: first-touch capture,
 * identity convergence and pageviews. The SDK itself is started earlier, by
 * `instrumentation-client.ts`.
 *
 * @returns nothing; it renders no UI.
 */
export function AnalyticsLifecycle() {
  const { snapshot } = useAuth();
  const trpc = useTRPC();
  const pathname = usePathname();

  // Capture first-touch attribution once per load. The SDK chunk is already
  // on its way (`instrumentation-client.ts`); this awaits the same instance.
  useEffect(() => {
    // Snapshot the query string BEFORE the async import resolves: a CTA
    // click can client-navigate `/?utm_…` → `/app` while the chunk loads,
    // blanking window.location.search and losing the first touch.
    const search = window.location.search;
    getAnalytics()
      .then((analytics) => analytics.captureFirstTouchUtm(search))
      .catch(() => undefined);
  }, []);

  // Identity for Mixpanel is the app user id (`String(me.id)`), fetched only
  // once the session is authenticated (skipped while `loading` and when
  // anonymous).
  const meEnabled = snapshot.status === "authenticated";
  const me = useQuery({ ...trpc.me.get.queryOptions(), enabled: meEnabled });
  const identity = meEnabled && me.data ? String(me.data.id) : null;
  // The account email classifies this browser's traffic as the server does.
  const email = meEnabled && me.data ? me.data.email : null;

  // The same address tells the ad pixels whether this browser is one of ours
  // (card #1113). Kept when the session ends: see `internalAccount.ts`.
  useEffect(() => {
    if (email !== null) rememberAccountTraffic(isInternalAccountEmail(email));
  }, [email]);

  // "Ready" = auth state resolved far enough that the pageview's identity is
  // final: anonymous immediately; authenticated once `me` settles (either way).
  const ready =
    snapshot.status === "anonymous" || (meEnabled && (me.data !== undefined || me.isError));

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    getAnalytics()
      .then((analytics) => {
        if (cancelled) return;
        // Core owns the previous-identity bookkeeping (in-memory + persisted),
        // so a sign-out interrupted on a PREVIOUS page load is repaired here
        // too — not just transitions this component observed.
        analytics.reconcileIdentity(identity, email);
        analytics.trackPageview(pathname, identity);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [ready, identity, email, pathname]);

  return null;
}
